import { useState, useMemo } from "react";
import BlogNavbar from "./components/BlogNavbar";
import BlogHero from "./components/BlogHero";
import TrendingStrip from "./components/TrendingStrip";
import CategoryShowcase from "./components/CategoryShowcase";
import BlogGrid from "./components/BlogGrid";
import NewsletterBanner from "./components/NewsletterBanner";
import AuthorSpotlight from "./components/AuthorSpotlight";
import Footer from "../home/components/Footer";
import JsonLd from "../../components/feature/JsonLd";
import { useSeoMeta } from "../../hooks/useSeoMeta";

const SITE_URL = import.meta.env.VITE_SITE_URL ?? "https://ltfsourcing.com";

export default function BlogPage() {
  const [search, setSearch] = useState("");

  useSeoMeta({
    title: "Sourcing & Supply Chain Blog | LTF Sourcing",
    description:
      "Expert guides on product sourcing, wholesale buying, inventory management, logistics, and supply chain strategy. Updated weekly by the LTF Sourcing team.",
    canonical: `${SITE_URL}/blog`,
    ogTitle: "Sourcing & Supply Chain Blog | LTF Sourcing",
    ogDescription:
      "Practical playbooks on global product sourcing, wholesale supply, overstock liquidation, incoterms, and cross-border logistics — from the LTF Sourcing team.",
    ogType: "website",
    keywords:
      "product sourcing blog, supply chain guides, wholesale buying tips, inventory management, logistics strategy, overstock clearance",
  });

  const collectionPageSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Sourcing & Supply Chain Blog — LTF Sourcing",
      description:
        "Expert articles on product sourcing, wholesale supply, inventory management, logistics, and supply chain strategy.",
      url: `${SITE_URL}/blog`,
      publisher: {
        "@type": "Organization",
        name: "LTF Sourcing",
        url: SITE_URL,
      },
      inLanguage: "en",
      about: [
        { "@type": "Thing", name: "Product Sourcing" },
        { "@type": "Thing", name: "Wholesale Supply" },
        { "@type": "Thing", name: "Inventory Management" },
        { "@type": "Thing", name: "Logistics" },
        { "@type": "Thing", name: "Supply Chain Strategy" },
      ],
    }),
    []
  );

  const breadcrumbSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
      ],
    }),
    []
  );

  const handleSearch = (val: string) => {
    setSearch(val);
    setTimeout(() => {
      const el = document.getElementById("blog-grid");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  return (
    <main className="min-h-screen w-full bg-white">
      <JsonLd id="schema-blog-collection" schema={collectionPageSchema} />
      <JsonLd id="schema-blog-breadcrumb" schema={breadcrumbSchema} />
      <BlogNavbar />
      <BlogHero search={search} onSearch={handleSearch} />
      <TrendingStrip />
      <CategoryShowcase />
      <div id="blog-grid">
        <BlogGrid search={search} activeCategory="All" />
      </div>
      <NewsletterBanner />
      <AuthorSpotlight />
      <Footer />
    </main>
  );
}
